import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { useHubToast } from "@/components/ui/HubToast";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { OpenInventoryDiscrepancySkuBadge } from "@/components/manufacturing/OpenInventoryDiscrepancySkuBadge";
import { MegaventoryProductExternalLink } from "@/components/mrp/MegaventoryPurchaseOrderLinks";
import { KittingInventoryDiscrepancyModal } from "@/pages/manufacturing/KittingInventoryDiscrepancyModal";
import { KittingInventoryLocationChangeButton } from "@/pages/manufacturing/KittingInventoryLocationChangeButton";
import { HoverHintContent, HoverHintWithGlyph } from "@/components/ui/title-help";
import { BomExplosionCard } from "@/pages/bom/BomBrowser";
import { AmlCpnDetailView } from "@/pages/mrp/AmlCpnDetail";
import { SkuDocumentsTab } from "@/pages/mrp/SkuDocumentsTab";
import { SkuRelatedQuotesTab } from "@/pages/mrp/SkuRelatedQuotesTab";
import { SkuTransactionHistoryTab } from "@/pages/mrp/SkuTransactionHistoryTab";
import { SkuPropertiesForm } from "@/pages/mrp/SkuEditPanel";
import { SkuQuoteDeployReviewBanner } from "@/pages/mrp/SkuQuoteDeployReviewBanner";
import { SkuShoppingTab } from "@/pages/mrp/SkuShoppingTab";
import {
  inventorySnapshotSublocationPresentation,
  type InventorySnapshotContainerRow,
} from "@/pages/mrp/inventorySnapshotSublocation";
import { skuDetailSgsLocationChangeSelection } from "@/pages/mrp/skuDetailSgsLocationChange";
import { InventoryWorkOrderLinesHoverTable } from "@/pages/mrp/SkuDetailInventoryWorkOrderHover";
import {
  isMrpSkuDetailTab,
  MRP_INVENTORY_SKU_REFRESH_MAX_AGE_MS,
  MRP_INVENTORY_STOCK_PHYSICAL_AVAILABLE_SHORT_HINT,
  MRP_SKU_DETAIL_TABS,
  mrpInventorySetVerifiedAsOf,
  mrpInventoryIncomingWorkOrders,
  mrpInventoryOutgoingWorkOrders,
  mrpInventoryStockPhysicalAvailable,
  type MrpInventoryListResponse,
  type MrpInventoryOutgoingWorkOrderLine,
  type MrpInventoryRow,
  type MrpInventorySkuRefreshResponse,
  type MrpSkuDetail,
  type MrpSkuDetailTab,
  parseMrpSkuDetailPathParam,
} from "@/lib/mrp";
import { useOpenInventoryDiscrepancySkus } from "@/lib/useOpenInventoryDiscrepancySkus";
import { cn } from "@/lib/utils";

/** Primary inventory sites shown prominently at the top of the SKU detail inventory tab. */
const SKU_DETAIL_PRIMARY_INVENTORY_RANK: Record<string, number> = {
  kodiak: 0,
  suntronic: 1,
  sgs: 2,
  sgst: 3,
};

function normalizeInventoryLocationCode(code: string): string {
  return code.trim().toLowerCase();
}

function skuDetailInventoryLocationDisplayName(row: MrpInventoryRow): string {
  if (normalizeInventoryLocationCode(row.location_code) === "sgst") {
    return "In-transit";
  }
  return row.location_name ?? row.location_code;
}

function isSkuDetailPrimaryInventoryRow(row: MrpInventoryRow): boolean {
  return normalizeInventoryLocationCode(row.location_code) in SKU_DETAIL_PRIMARY_INVENTORY_RANK;
}

function compareSkuDetailPrimaryInventoryRows(a: MrpInventoryRow, b: MrpInventoryRow): number {
  const left = SKU_DETAIL_PRIMARY_INVENTORY_RANK[normalizeInventoryLocationCode(a.location_code)] ?? 100;
  const right = SKU_DETAIL_PRIMARY_INVENTORY_RANK[normalizeInventoryLocationCode(b.location_code)] ?? 100;
  return left - right;
}

function compareSkuDetailSecondaryInventoryRows(a: MrpInventoryRow, b: MrpInventoryRow): number {
  return (a.location_name ?? a.location_code).localeCompare(b.location_name ?? b.location_code, undefined, {
    sensitivity: "base",
  });
}

const INVENTORY_SNAPSHOT_HOVER_HINT_ARIA =
  "Inventory snapshot: latest Megaventory stock per location from mrp.inventory_snapshot, table layout, column definitions, and as-of timestamp rules.";

const INVENTORY_SNAPSHOT_SUB_LOCATION_HOVER_HINT =
  "Live inventory containers at this site. Hover lists each container. The cell joins the unique bins from that list.";

const INVENTORY_SNAPSHOT_PHYSICAL_HOVER_HINT =
  "Megaventory physical quantity. Hover for StockPhysical breakdown.";

const INVENTORY_SNAPSHOT_WIP_HOVER_HINT =
  "Hub-only quantity soft-allocated to open hub work orders. Megaventory allocations are already out of physical and available. Hover for open hub work order breakdown.";

const INVENTORY_SNAPSHOT_AVAILABLE_HOVER_HINT = MRP_INVENTORY_STOCK_PHYSICAL_AVAILABLE_SHORT_HINT;

const INVENTORY_SNAPSHOT_RESERVE_HOVER_HINT =
  "Planning reserve for this site. Hover for mrp.reserve breakdown.";

const INVENTORY_SNAPSHOT_OUTGOING_SALES_ORDERS_HOVER_HINT =
  "Open sales order demand not yet shipped. Hover for StockNonShipped breakdown.";

const INVENTORY_SNAPSHOT_OUTGOING_WORK_ORDERS_HOVER_HINT =
  "Pending WO demand not yet soft-allocated. Hover for open WO demand minus WIP breakdown.";

const INVENTORY_SNAPSHOT_MRP_ON_HAND_HOVER_HINT =
  "Forecast starting quantity for this site. Hover for the full breakdown.";

const INVENTORY_SNAPSHOT_INCOMING_PURCHASE_ORDERS_HOVER_HINT =
  "Open purchase order quantity not yet received. Hover for StockNonReceivedPOs breakdown.";

const INVENTORY_SNAPSHOT_INCOMING_WORK_ORDERS_HOVER_HINT =
  "Open work order finished goods not yet received (Megaventory and hub-native). Hover for StockNonReceivedWOs breakdown.";

const INVENTORY_SNAPSHOT_NET_HOVER_HINT =
  "Net stock at this site. Hover for Physical, outgoing, and incoming breakdown.";

const INVENTORY_SNAPSHOT_COLUMN_HELP: { label: string; hint: string }[] = [
  { label: "Sub location", hint: INVENTORY_SNAPSHOT_SUB_LOCATION_HOVER_HINT },
  { label: "Physical", hint: INVENTORY_SNAPSHOT_PHYSICAL_HOVER_HINT },
  { label: "WIP", hint: INVENTORY_SNAPSHOT_WIP_HOVER_HINT },
  { label: "Available", hint: INVENTORY_SNAPSHOT_AVAILABLE_HOVER_HINT },
  { label: "Reserve", hint: INVENTORY_SNAPSHOT_RESERVE_HOVER_HINT },
  { label: "Outgoing on sales orders", hint: INVENTORY_SNAPSHOT_OUTGOING_SALES_ORDERS_HOVER_HINT },
  { label: "Outgoing on work orders", hint: INVENTORY_SNAPSHOT_OUTGOING_WORK_ORDERS_HOVER_HINT },
  { label: "MRP on hand", hint: INVENTORY_SNAPSHOT_MRP_ON_HAND_HOVER_HINT },
  { label: "Incoming on purchase orders", hint: INVENTORY_SNAPSHOT_INCOMING_PURCHASE_ORDERS_HOVER_HINT },
  { label: "Incoming on work orders", hint: INVENTORY_SNAPSHOT_INCOMING_WORK_ORDERS_HOVER_HINT },
  { label: "NET", hint: INVENTORY_SNAPSHOT_NET_HOVER_HINT },
];

const inventoryHoverTableClass =
  "w-max max-w-[32rem] border-collapse text-left text-[11px] leading-snug text-slate-700";
const inventoryHoverThClass = "px-2 pb-1 font-medium text-slate-500 whitespace-nowrap";
const inventoryHoverTdClass = "px-2 py-0.5 align-top";
const inventoryHoverTdMonoClass = cn(inventoryHoverTdClass, "font-mono text-[10px]");

type InventoryFormulaHoverRow = {
  element: string;
  sign: "+" | "−";
  detail: string;
};

function inventoryFormulaSignClass(sign: "+" | "−" | "="): string {
  if (sign === "+") {
    return "text-emerald-700";
  }
  if (sign === "−") {
    return "text-rose-700";
  }
  return "text-slate-900";
}

function InventoryFormulaColumnHoverHint({
  addsIntoLabel,
  rows,
  resultLabel,
  resultDetail,
}: {
  addsIntoLabel: string;
  rows: InventoryFormulaHoverRow[];
  resultLabel: string;
  resultDetail: string;
}): JSX.Element {
  return (
    <div className="text-left">
      <table className={cn(inventoryHoverTableClass, "table-fixed min-w-[26rem]")}>
        <colgroup>
          <col className="w-[7.5rem]" />
          <col className="w-[4.5rem]" />
          <col />
        </colgroup>
        <thead>
          <tr className="border-b border-slate-200">
            <th className={inventoryHoverThClass}>Adds into {addsIntoLabel}</th>
            <th className={cn(inventoryHoverThClass, "text-center")}>±</th>
            <th className={inventoryHoverThClass}>Inventory column</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.element}>
              <td className={cn(inventoryHoverTdClass, "font-medium text-slate-800")}>{row.element}</td>
              <td
                className={cn(
                  inventoryHoverTdClass,
                  "text-center tabular-nums font-medium",
                  inventoryFormulaSignClass(row.sign),
                )}
              >
                {row.sign}
              </td>
              <td className={inventoryHoverTdClass}>{row.detail}</td>
            </tr>
          ))}
          <tr className="border-t border-slate-100">
            <td className={cn(inventoryHoverTdClass, "font-semibold text-slate-900")}>{resultLabel}</td>
            <td
              className={cn(
                inventoryHoverTdClass,
                "text-center font-semibold",
                inventoryFormulaSignClass("="),
              )}
            >
              =
            </td>
            <td className={inventoryHoverTdClass}>{resultDetail}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function InventorySubLocationHoverHint(): JSX.Element {
  return (
    <InventoryFormulaColumnHoverHint
      addsIntoLabel="Sub location"
      rows={[
        {
          element: "Inventory containers",
          sign: "+",
          detail: "Each live container at this site, with its quantity and bin",
        },
      ]}
      resultLabel="Sub location"
      resultDetail="Unique bins from that list, concatenated"
    />
  );
}

function InventoryPhysicalHoverHint(): JSX.Element {
  return (
    <InventoryFormulaColumnHoverHint
      addsIntoLabel="Physical"
      rows={[
        {
          element: "Megaventory ingest",
          sign: "+",
          detail: "StockPhysical",
        },
      ]}
      resultLabel="Physical"
      resultDetail="Megaventory physical quantity"
    />
  );
}

function InventoryWipHoverHint(): JSX.Element {
  return (
    <InventoryFormulaColumnHoverHint
      addsIntoLabel="WIP"
      rows={[
        {
          element: "Soft-allocated demand",
          sign: "+",
          detail: "Open work orders (hub and Megaventory)",
        },
      ]}
      resultLabel="WIP"
      resultDetail="Quantity soft-allocated to open work orders"
    />
  );
}

function InventoryAvailableHoverHint(): JSX.Element {
  return (
    <InventoryFormulaColumnHoverHint
      addsIntoLabel="Available"
      rows={[
        {
          element: "Physical stock",
          sign: "+",
          detail: "Megaventory physical quantity",
        },
        {
          element: "WIP",
          sign: "−",
          detail: "Soft-allocated on open work orders",
        },
      ]}
      resultLabel="Available"
      resultDetail="Free shelf stock at this site"
    />
  );
}

function InventoryReserveHoverHint(): JSX.Element {
  return (
    <InventoryFormulaColumnHoverHint
      addsIntoLabel="Reserve"
      rows={[
        {
          element: "Planning hold",
          sign: "+",
          detail: "mrp.reserve for this site",
        },
      ]}
      resultLabel="Reserve"
      resultDetail="Planning reserve for this site"
    />
  );
}

function InventoryOutgoingSalesOrdersHoverHint(): JSX.Element {
  return (
    <InventoryFormulaColumnHoverHint
      addsIntoLabel="Outgoing on sales orders"
      rows={[
        {
          element: "Open sales orders",
          sign: "+",
          detail: "Megaventory StockNonShipped",
        },
      ]}
      resultLabel="Outgoing on sales orders"
      resultDetail="Open sales order demand not yet shipped"
    />
  );
}

function InventoryOutgoingWorkOrdersHoverHint(): JSX.Element {
  return (
    <InventoryFormulaColumnHoverHint
      addsIntoLabel="Outgoing on work orders"
      rows={[
        {
          element: "Open WO demand",
          sign: "+",
          detail: "Open work-order BOM demand (hub-native and legacy Megaventory)",
        },
        {
          element: "WIP",
          sign: "−",
          detail: "Soft-allocated on open work orders",
        },
      ]}
      resultLabel="Outgoing on work orders"
      resultDetail="Pending WO demand not yet soft-allocated"
    />
  );
}

function InventoryMrpOnHandHoverHint(): JSX.Element {
  return (
    <InventoryFormulaColumnHoverHint
      addsIntoLabel="MRP on hand"
      rows={[
        {
          element: "Physical stock",
          sign: "+",
          detail: "Megaventory physical quantity",
        },
        {
          element: "Planning reserve",
          sign: "−",
          detail: "Reserve",
        },
        {
          element: "Outgoing on sales orders",
          sign: "−",
          detail: "Outgoing on sales orders",
        },
        {
          element: "Outgoing on work orders",
          sign: "−",
          detail: "Pending WO demand not yet soft-allocated",
        },
      ]}
      resultLabel="MRP on hand"
      resultDetail="Forecast starting quantity for this site"
    />
  );
}

function InventoryIncomingPurchaseOrdersHoverHint(): JSX.Element {
  return (
    <InventoryFormulaColumnHoverHint
      addsIntoLabel="Incoming on purchase orders"
      rows={[
        {
          element: "Open purchase orders",
          sign: "+",
          detail: "Megaventory StockNonReceivedPOs",
        },
      ]}
      resultLabel="Incoming on purchase orders"
      resultDetail="Open purchase order quantity not yet received"
    />
  );
}

function InventoryIncomingWorkOrdersHoverHint(): JSX.Element {
  return (
    <InventoryFormulaColumnHoverHint
      addsIntoLabel="Incoming on work orders"
      rows={[
        {
          element: "Megaventory open WO receipts",
          sign: "+",
          detail: "Megaventory StockNonReceivedWOs",
        },
        {
          element: "Hub-native open WO receipts",
          sign: "+",
          detail: "Remaining ordered minus received on open hub-native work orders",
        },
      ]}
      resultLabel="Incoming on work orders"
      resultDetail="Open work order finished goods not yet received"
    />
  );
}

function InventoryNetHoverHint(): JSX.Element {
  return (
    <InventoryFormulaColumnHoverHint
      addsIntoLabel="NET"
      rows={[
        {
          element: "Physical stock",
          sign: "+",
          detail: "Megaventory physical quantity",
        },
        {
          element: "Outgoing on sales orders",
          sign: "−",
          detail: "Outgoing on sales orders",
        },
        {
          element: "Outgoing on work orders",
          sign: "−",
          detail: "Pending WO demand not yet soft-allocated",
        },
        {
          element: "Incoming on purchase orders",
          sign: "+",
          detail: "Incoming on purchase orders",
        },
        {
          element: "Incoming on work orders",
          sign: "+",
          detail: "Incoming on work orders",
        },
      ]}
      resultLabel="NET"
      resultDetail="Net stock at this site"
    />
  );
}

function InventorySublocationHoverTable({
  rows,
}: {
  rows: InventorySnapshotContainerRow[];
}): JSX.Element {
  return (
    <table className={cn(inventoryHoverTableClass, "table-fixed")}>
      <colgroup>
        <col className="w-[4.5rem]" />
        <col className="w-[8rem]" />
        <col className="w-[6.5rem]" />
      </colgroup>
      <thead>
        <tr className="border-b border-slate-200">
          <th className={cn(inventoryHoverThClass, "text-right")}>Qty</th>
          <th className={inventoryHoverThClass}>Bin</th>
          <th className={inventoryHoverThClass}>Container</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((entry) => (
          <tr key={entry.id}>
            <td className={cn(inventoryHoverTdClass, "text-right tabular-nums")}>
              {entry.quantity.toLocaleString()}
            </td>
            <td className={inventoryHoverTdMonoClass}>{entry.sublocation}</td>
            <td className={inventoryHoverTdMonoClass}>{entry.shortCode}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InventorySnapshotColumnHelpTable(): JSX.Element {
  return (
    <table className={cn(inventoryHoverTableClass, "min-w-[28rem]")}>
      <thead>
        <tr className="border-b border-slate-200">
          <th className={inventoryHoverThClass}>Column</th>
          <th className={inventoryHoverThClass}>Meaning</th>
        </tr>
      </thead>
      <tbody>
        {INVENTORY_SNAPSHOT_COLUMN_HELP.map(({ label, hint }) => (
          <tr key={label}>
            <td className={cn(inventoryHoverTdClass, "whitespace-nowrap font-medium text-slate-800")}>
              {label}
            </td>
            <td className={inventoryHoverTdClass}>{hint}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InventorySnapshotHeaderLabel({
  lines,
  hint,
  align = "left",
}: {
  lines: string[];
  hint?: ReactNode;
  align?: "left" | "right";
}): JSX.Element {
  const label = (
    <span className={cn("inline-block leading-snug", align === "right" && "text-right")}>
      {lines.map((line) => (
        <span key={line} className="block">{line}</span>
      ))}
    </span>
  );
  if (hint == null) {
    return label;
  }
  return (
    <HoverHintContent hoverHint={hint} align={align === "right" ? "center" : "start"}>
      {label}
    </HoverHintContent>
  );
}

function InventorySnapshotHoverHintContent(): JSX.Element {
  return (
    <div className="max-w-sm space-y-3">
      <p>
        Latest row per Megaventory inventory location from{" "}
        <code className="rounded bg-slate-100 px-1 font-mono text-[0.85em]">mrp.inventory_snapshot</code> (integrator
        inventory ingest) for this CPN.
      </p>
      <p>
        <span className="font-semibold text-slate-800">Layout.</span> Kodiak, Suntronic, SGS, and In-transit (SGST) are
        listed first; other contract-manufacturer sites follow below in a compact section. Forecast uses only the three
        MRP planning sites (Kodiak, SGS, Suntronic).
      </p>
      <div className="space-y-1">
        <p className="font-semibold text-slate-800">Columns</p>
        <InventorySnapshotColumnHelpTable />
      </div>
      <p>
        <span className="font-semibold text-slate-800">As of</span> — newer of the last successful full Megaventory
        inventory sync and this SKU&apos;s last verified row update. The tab shows the hub snapshot immediately, then
        refreshes this SKU from Megaventory in the background when older than 10 seconds (or when you reload the
        snapshot). Shown in your local timezone.
      </p>
    </div>
  );
}

function InventorySnapshotSublocationCell({
  containers,
  variant = "primary",
}: {
  containers: InventorySnapshotContainerRow[] | undefined;
  variant?: "primary" | "secondary";
}): JSX.Element {
  const primary = variant === "primary";
  const cellClass = cn("px-2 align-top text-slate-800", primary ? "py-3 text-sm" : "py-1.5 text-xs");
  const presentation = inventorySnapshotSublocationPresentation(containers);

  if (presentation.rows.length === 0) {
    return <td className={cellClass}>{presentation.label}</td>;
  }

  return (
    <td className={cellClass}>
      <HoverHintContent
        align="start"
        cursor="default"
        block
        hoverHint={<InventorySublocationHoverTable rows={presentation.rows} />}
      >
        <div className="font-mono">{presentation.label}</div>
      </HoverHintContent>
    </td>
  );
}

function InventorySnapshotWipCell({
  wip,
  lines,
  variant = "primary",
}: {
  wip: number;
  lines: MrpInventoryOutgoingWorkOrderLine[];
  variant?: "primary" | "secondary";
}): JSX.Element {
  const formatted = wip.toLocaleString();
  const cellClass = cn(
    "px-2 text-right font-mono font-medium text-amber-800",
    variant === "primary" ? "py-3 text-sm" : "py-1.5 text-xs",
  );
  if (wip <= 0) {
    return <td className={cellClass}>{formatted}</td>;
  }
  const hoverBody =
    lines.length > 0 ? (
      <InventoryWorkOrderLinesHoverTable lines={lines} />
    ) : (
      <p className="text-left text-sm">
        {wip.toLocaleString()} soft-allocated on open work orders at this site (per-work-order
        breakdown unavailable).
      </p>
    );
  return (
    <td className={cellClass}>
      <HoverHintContent
        align="center"
        cursor="default"
        block
        className="justify-end"
        hoverHint={hoverBody}
      >
        <span>{formatted}</span>
      </HoverHintContent>
    </td>
  );
}

function InventoryOutgoingWorkOrdersCell({
  total,
  lines,
  variant = "primary",
}: {
  total: number;
  lines: MrpInventoryOutgoingWorkOrderLine[] | undefined;
  variant?: "primary" | "secondary";
}): JSX.Element {
  const formatted = total.toLocaleString();
  const cellClass = cn(
    "px-2 text-right font-mono font-medium text-red-600",
    variant === "primary" ? "py-3 text-sm" : "py-1.5 text-xs",
  );
  if (total <= 0 || lines == null || lines.length === 0) {
    return <td className={cellClass}>{formatted}</td>;
  }
  return (
    <td className={cellClass}>
      <HoverHintContent
        align="center"
        cursor="default"
        block
        className="justify-end"
        hoverHint={<InventoryWorkOrderLinesHoverTable lines={lines} />}
      >
        <span>{formatted}</span>
      </HoverHintContent>
    </td>
  );
}

function InventoryIncomingWorkOrdersCell({
  total,
  lines,
  variant = "primary",
}: {
  total: number;
  lines: MrpInventoryOutgoingWorkOrderLine[] | undefined;
  variant?: "primary" | "secondary";
}): JSX.Element {
  const formatted = total.toLocaleString();
  const cellClass = cn(
    "px-2 text-right font-mono font-medium text-emerald-700",
    variant === "primary" ? "py-3 text-sm" : "py-1.5 text-xs",
  );
  if (total <= 0 || lines == null || lines.length === 0) {
    return <td className={cellClass}>{formatted}</td>;
  }
  return (
    <td className={cellClass}>
      <HoverHintContent
        align="center"
        cursor="default"
        block
        className="justify-end"
        hoverHint={<InventoryWorkOrderLinesHoverTable lines={lines} />}
      >
        <span>{formatted}</span>
      </HoverHintContent>
    </td>
  );
}

function InventorySnapshotRow({
  row,
  variant,
  isFirstSecondary,
}: {
  row: MrpInventoryRow;
  variant: "primary" | "secondary";
  isFirstSecondary?: boolean;
}): JSX.Element {
  const inPo = row.incoming_purchase_orders ?? 0;
  const inWo = mrpInventoryIncomingWorkOrders(row);
  const outSo = row.outgoing_sales_orders ?? 0;
  const wip = row.hub_soft_allocated_wip ?? 0;
  const outWo = mrpInventoryOutgoingWorkOrders(row);
  const phys = row.stock_physical ?? 0;
  const available = mrpInventoryStockPhysicalAvailable(row);
  const reserve = row.reserve_allocated ?? 0;
  const netSum = phys - outSo - outWo + inPo + inWo;
  const wipLines = (row.outgoing_work_order_lines ?? []).filter(
    (line) => line.demand_kind === "soft_allocated",
  );
  const outgoingWoLines = (row.outgoing_work_order_lines ?? []).filter(
    (line) => line.demand_kind !== "soft_allocated",
  );
  const primary = variant === "primary";

  return (
    <tr
      className={cn(
        "transition-colors",
        primary
          ? "bg-white hover:bg-slate-50/80 hover:[&>td]:bg-slate-50/80"
          : "bg-slate-50/40 text-slate-600 hover:bg-slate-100/70 hover:[&>td]:bg-slate-100/70",
        isFirstSecondary ? "border-t-2 border-slate-300" : undefined,
      )}
    >
      <td className={cn("px-2 text-left text-slate-800", primary ? "py-3 text-sm" : "py-1.5 text-xs")}>
        <div className={cn(primary ? "font-semibold" : "font-medium")}>
          {skuDetailInventoryLocationDisplayName(row)}
        </div>
        {row.location_abbreviation &&
        row.location_abbreviation.trim().toLowerCase() !== String(row.location_code).trim().toLowerCase() ? (
          <div className={cn("font-mono uppercase text-slate-500", primary ? "text-xs" : "text-[10px]")}>
            {row.location_abbreviation}
          </div>
        ) : null}
      </td>
      <InventorySnapshotSublocationCell
        containers={(row.sublocation_containers ?? []).map((container) => ({
          id: container.id,
          shortCode: container.short_code,
          quantity: container.quantity,
          sublocation: container.sublocation,
        }))}
        variant={variant}
      />
      <td
        className={cn(
          "px-2 text-right font-mono text-slate-900",
          primary ? "py-3 text-sm" : "py-1.5 text-xs",
        )}
      >
        {phys.toLocaleString()}
      </td>
      <InventorySnapshotWipCell wip={wip} lines={wipLines} variant={variant} />
      <td
        className={cn(
          "px-2 text-right font-mono text-slate-900",
          primary ? "py-3 text-sm" : "py-1.5 text-xs",
        )}
      >
        {available.toLocaleString()}
      </td>
      <td
        className={cn(
          "px-2 text-right font-mono font-medium text-red-600",
          primary ? "py-3 text-sm" : "py-1.5 text-xs",
        )}
      >
        {reserve.toLocaleString()}
      </td>
      <td
        className={cn(
          "px-2 text-right font-mono font-medium text-red-600",
          primary ? "py-3 text-sm" : "py-1.5 text-xs",
        )}
      >
        {outSo.toLocaleString()}
      </td>
      <InventoryOutgoingWorkOrdersCell
        total={outWo}
        lines={outgoingWoLines}
        variant={variant}
      />
      <td
        className={cn(
          "px-2 text-right font-mono text-slate-900",
          primary ? "py-3 text-sm" : "py-1.5 text-xs",
        )}
      >
        {(row.planning_on_hand ?? row.quantity).toLocaleString()}
      </td>
      <td
        className={cn(
          "px-2 text-right font-mono font-medium text-emerald-700",
          primary ? "py-3 text-sm" : "py-1.5 text-xs",
        )}
      >
        {inPo.toLocaleString()}
      </td>
      <InventoryIncomingWorkOrdersCell
        total={inWo}
        lines={row.incoming_work_order_lines}
        variant={variant}
      />
      <td
        className={cn(
          "px-2 text-right font-mono text-slate-900",
          primary ? "py-3 text-sm" : "py-1.5 text-xs",
        )}
      >
        {netSum.toLocaleString()}
      </td>
    </tr>
  );
}

interface BomResponse {
  sku: string;
  rows: {
    finished_good_sku: string;
    template_id: number | null;
    revision: string | null;
    variable_name: string | null;
    quantity: number;
    component_sku: string;
    manufacture: boolean;
  }[];
}

export function SkuDetail() {
  const { "*": skuPathParam = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sku = parseMrpSkuDetailPathParam(skuPathParam);
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const canWriteManufacturing = useHubModuleWrite("manufacturing");
  const [discrepancyModalOpen, setDiscrepancyModalOpen] = useState(false);
  const softRefreshStartedForSkuRef = useRef<string | null>(null);

  usePageBreadcrumb(sku.length > 0 ? sku : "SKU detail");

  const skusForDiscrepancyLookup = useMemo(() => (sku.length > 0 ? [sku] : []), [sku]);
  const { hasOpenDiscrepancy } = useOpenInventoryDiscrepancySkus(skusForDiscrepancyLookup);

  const skuDetailQuery = useQuery<MrpSkuDetail, ApiError>({
    queryKey: ["mrp", "skus", "detail", sku],
    queryFn: () => apiFetch<MrpSkuDetail>("/api/mrp/skus", { query: { sku } }),
    enabled: sku.length > 0,
    refetchOnWindowFocus: false,
  });
  const skuMegaventoryProductId =
    skuDetailQuery.data?.mv_id != null ? Number(skuDetailQuery.data.mv_id) : NaN;

  const visibleTabs = useMemo(() => {
    const tabs = [...MRP_SKU_DETAIL_TABS];
    tabs.push({ id: "documents", label: "Related documents" });
    tabs.push({ id: "quotes", label: "Related quotes" });
    tabs.push({ id: "transaction_history", label: "Transaction history" });
    return tabs;
  }, []);

  const tabParamRaw = searchParams.get("tab");
  const tabParam =
    tabParamRaw === "quick_links" || tabParamRaw === "mouser_test" ? "shopping" : tabParamRaw;
  const activeTab: MrpSkuDetailTab =
    isMrpSkuDetailTab(tabParam) ? tabParam : "properties";

  const setTab = (next: MrpSkuDetailTab): void => {
    setSearchParams(
      (previous) => {
        const merged = new URLSearchParams(previous);
        if (next === "properties") {
          merged.delete("tab");
        } else {
          merged.set("tab", next);
        }
        return merged;
      },
      { replace: true },
    );
  };

  const bomQuery = useQuery<BomResponse, ApiError>({
    queryKey: ["mrp", "bom", "explode", sku],
    queryFn: () => apiFetch<BomResponse>("/api/mrp/bom", { query: { sku } }),
    enabled: sku.length > 0 && activeTab === "bom",
    refetchOnWindowFocus: false,
  });

  const inventoryQuery = useQuery<MrpInventoryListResponse, ApiError>({
    queryKey: ["mrp", "inventory", "sku-detail", sku],
    queryFn: () => apiFetch<MrpInventoryListResponse>("/api/mrp/inventory", { query: { cpn: sku } }),
    enabled: sku.length > 0 && activeTab === "inventory",
    refetchOnWindowFocus: false,
  });

  const softRefreshInventoryMutation = useMutation<MrpInventorySkuRefreshResponse, ApiError>({
    mutationFn: () =>
      apiFetch<MrpInventorySkuRefreshResponse>("/api/mrp/inventory/refresh-sku", {
        method: "POST",
        body: { cpn: sku, max_age_ms: MRP_INVENTORY_SKU_REFRESH_MAX_AGE_MS },
      }),
    onSuccess: async (result) => {
      if (!result.refreshed) return;
      await queryClient.invalidateQueries({ queryKey: ["mrp", "inventory", "sku-detail", sku] });
    },
    onError: (error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const reloadInventoryMutation = useMutation<MrpInventorySkuRefreshResponse, ApiError>({
    mutationFn: async () => {
      const result = await apiFetch<MrpInventorySkuRefreshResponse>("/api/mrp/inventory/refresh-sku", {
        method: "POST",
        body: { cpn: sku, force: true },
      });
      await queryClient.invalidateQueries({ queryKey: ["mrp", "inventory", "sku-detail", sku] });
      await queryClient.refetchQueries({ queryKey: ["mrp", "inventory", "sku-detail", sku] });
      return result;
    },
    onError: (error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  useEffect(() => {
    if (activeTab !== "inventory") {
      softRefreshStartedForSkuRef.current = null;
      return;
    }
    if (sku.length === 0) return;
    if (!inventoryQuery.isSuccess) return;
    if (softRefreshStartedForSkuRef.current === sku) return;
    softRefreshStartedForSkuRef.current = sku;
    softRefreshInventoryMutation.mutate();
    // One background Megaventory soft-refresh per inventory-tab visit for this SKU.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot after first snapshot paint
  }, [activeTab, sku, inventoryQuery.isSuccess]);

  const inventoryRefreshing =
    softRefreshInventoryMutation.isPending || reloadInventoryMutation.isPending;

  const rows = inventoryQuery.data?.rows ?? [];
  const { primaryInventoryRows, secondaryInventoryRows } = useMemo(() => {
    const primary: MrpInventoryRow[] = [];
    const secondary: MrpInventoryRow[] = [];
    for (const row of rows) {
      if (isSkuDetailPrimaryInventoryRow(row)) {
        primary.push(row);
      } else {
        secondary.push(row);
      }
    }
    primary.sort(compareSkuDetailPrimaryInventoryRows);
    secondary.sort(compareSkuDetailSecondaryInventoryRows);
    return { primaryInventoryRows: primary, secondaryInventoryRows: secondary };
  }, [rows]);
  const inventoryRowCount = primaryInventoryRows.length + secondaryInventoryRows.length;
  const sgsLocationChange = useMemo(
    () => skuDetailSgsLocationChangeSelection(sku, rows),
    [rows, sku],
  );

  /**
   * Verified-as-of for this SKU: newer of last full inventory ingest and per-row `updated_at`
   * (SKU-targeted Megaventory refresh always touches rows).
   */
  const inventorySnapshotAsOf = useMemo(
    () =>
      mrpInventorySetVerifiedAsOf(
        [sku],
        rows,
        inventoryQuery.data?.inventory_last_full_sync_at,
      ),
    [inventoryQuery.data?.inventory_last_full_sync_at, rows, sku],
  );

  if (sku.length === 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-600">Missing SKU in the URL.</p>
        <Link className="text-sm text-sky-700 underline hover:text-sky-900" to="/mrp/skus">
          Back to SKU list
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-slate-600">
          <Link className="text-sky-700 hover:text-sky-900" to="/mrp/skus">
            SKU list
          </Link>
          <span className="text-slate-400"> / </span>
          <span className="font-medium text-slate-800">Detail</span>
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="inline-flex items-center gap-1.5 font-mono text-xl font-semibold text-slate-900">
            {sku}
            {Number.isInteger(skuMegaventoryProductId) && skuMegaventoryProductId > 0 ? (
              <MegaventoryProductExternalLink productMvId={skuMegaventoryProductId} sku={sku} />
            ) : null}
          </h1>
          {hasOpenDiscrepancy(sku) ? <OpenInventoryDiscrepancySkuBadge sku={sku} /> : null}
          <SkuQuoteDeployReviewBanner sku={sku} />
        </div>
      </div>

      <div
        className="flex flex-wrap gap-1 border-b border-slate-200 pb-px"
        role="tablist"
        aria-label="SKU detail sections"
      >
        {visibleTabs.map((tab) => {
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              id={`sku-tab-${tab.id}`}
              aria-controls={`sku-panel-${tab.id}`}
              className={cn(
                "rounded-t-md px-3 py-2 text-sm font-medium transition-colors",
                selected
                  ? "relative z-10 border border-b-0 border-slate-200 bg-white text-slate-900 shadow-sm"
                  : "border border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
              )}
              onClick={() => setTab(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`sku-panel-${activeTab}`}
        aria-labelledby={`sku-tab-${activeTab}`}
        className="min-h-[12rem]"
      >
        {activeTab === "properties" ? <SkuPropertiesForm sku={sku} /> : null}

        {activeTab === "bom" ? <BomExplosionCard query={bomQuery} sku={sku} /> : null}

        {activeTab === "inventory" ? (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <CardTitle className="mb-0">Inventory snapshot</CardTitle>
                  <HoverHintWithGlyph
                    hoverHintAriaLabel={INVENTORY_SNAPSHOT_HOVER_HINT_ARIA}
                    hoverHintContent={<InventorySnapshotHoverHintContent />}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {canWriteManufacturing ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => setDiscrepancyModalOpen(true)}
                    >
                      <AlertTriangle className="h-4 w-4" aria-hidden />
                      Note inventory discrepancy
                    </Button>
                  ) : null}
                  <KittingInventoryLocationChangeButton
                    selection={sgsLocationChange.selection}
                    knownStorageLocations={sgsLocationChange.knownStorageLocations}
                    allowChangeSource
                    disabled={sku.length === 0}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => reloadInventoryMutation.mutate()}
                    disabled={sku.length === 0 || inventoryRefreshing}
                    aria-busy={inventoryRefreshing}
                  >
                    <RefreshCw
                      className={cn("h-4 w-4", inventoryRefreshing && "animate-spin")}
                      aria-hidden
                    />
                    Reload inventory snapshot
                  </Button>
                  {!inventoryQuery.isLoading && !inventoryQuery.error && inventoryRowCount > 0 ? (
                    <p className="text-sm text-slate-800">
                      <span className="text-slate-500">As of: </span>
                      {inventorySnapshotAsOf ? inventorySnapshotAsOf.toLocaleString() : "—"}
                    </p>
                  ) : null}
                </div>
              </div>
            </CardHeader>
            <CardBody className="overflow-x-auto p-0">
              {inventoryQuery.isLoading ? (
                <p className="px-4 py-6 text-sm text-slate-500">Loading…</p>
              ) : inventoryQuery.error ? (
                <p className="px-4 py-6 text-sm text-rose-700">{inventoryQuery.error.message}</p>
              ) : inventoryRowCount === 0 ? (
                <p className="px-4 py-6 text-sm text-slate-600">
                  No inventory snapshot rows for this CPN. If the part is new or not stocked, this can be empty.
                </p>
              ) : (
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="px-2 py-2 align-bottom">
                        <InventorySnapshotHeaderLabel lines={["Location"]} />
                      </th>
                      <th className="px-2 py-2 align-bottom">
                        <InventorySnapshotHeaderLabel
                          lines={["Sub", "location"]}
                          hint={<InventorySubLocationHoverHint />}
                        />
                      </th>
                      <th className="px-2 py-2 text-right align-bottom">
                        <InventorySnapshotHeaderLabel
                          lines={["Physical"]}
                          hint={<InventoryPhysicalHoverHint />}
                          align="right"
                        />
                      </th>
                      <th className="px-2 py-2 text-right align-bottom">
                        <InventorySnapshotHeaderLabel
                          lines={["WIP"]}
                          hint={<InventoryWipHoverHint />}
                          align="right"
                        />
                      </th>
                      <th className="px-2 py-2 text-right align-bottom">
                        <InventorySnapshotHeaderLabel
                          lines={["Available"]}
                          hint={<InventoryAvailableHoverHint />}
                          align="right"
                        />
                      </th>
                      <th className="px-2 py-2 text-right align-bottom">
                        <InventorySnapshotHeaderLabel
                          lines={["Reserve"]}
                          hint={<InventoryReserveHoverHint />}
                          align="right"
                        />
                      </th>
                      <th className="px-2 py-2 text-right align-bottom">
                        <InventorySnapshotHeaderLabel
                          lines={["Outgoing on", "sales orders"]}
                          hint={<InventoryOutgoingSalesOrdersHoverHint />}
                          align="right"
                        />
                      </th>
                      <th className="px-2 py-2 text-right align-bottom">
                        <InventorySnapshotHeaderLabel
                          lines={["Outgoing on", "work orders"]}
                          hint={<InventoryOutgoingWorkOrdersHoverHint />}
                          align="right"
                        />
                      </th>
                      <th className="px-2 py-2 text-right align-bottom">
                        <InventorySnapshotHeaderLabel
                          lines={["MRP on", "hand"]}
                          hint={<InventoryMrpOnHandHoverHint />}
                          align="right"
                        />
                      </th>
                      <th className="px-2 py-2 text-right align-bottom">
                        <InventorySnapshotHeaderLabel
                          lines={["Incoming on", "purchase orders"]}
                          hint={<InventoryIncomingPurchaseOrdersHoverHint />}
                          align="right"
                        />
                      </th>
                      <th className="px-2 py-2 text-right align-bottom">
                        <InventorySnapshotHeaderLabel
                          lines={["Incoming on", "work orders"]}
                          hint={<InventoryIncomingWorkOrdersHoverHint />}
                          align="right"
                        />
                      </th>
                      <th className="px-2 py-2 text-right align-bottom">
                        <InventorySnapshotHeaderLabel
                          lines={["NET"]}
                          hint={<InventoryNetHoverHint />}
                          align="right"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {primaryInventoryRows.map((row) => (
                      <InventorySnapshotRow key={`${row.location_id ?? row.location_code}`} row={row} variant="primary" />
                    ))}
                    {secondaryInventoryRows.map((row, index) => (
                      <InventorySnapshotRow
                        key={`${row.location_id ?? row.location_code}`}
                        row={row}
                        variant="secondary"
                        isFirstSecondary={index === 0 && primaryInventoryRows.length > 0}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>
        ) : null}

        {activeTab === "aml" ? <AmlCpnDetailView cpn={sku} /> : null}

        {activeTab === "shopping" ? <SkuShoppingTab cpn={sku} /> : null}

        {activeTab === "documents" ? <SkuDocumentsTab sku={sku} /> : null}

        {activeTab === "quotes" ? <SkuRelatedQuotesTab sku={sku} /> : null}

        {activeTab === "transaction_history" ? <SkuTransactionHistoryTab sku={sku} /> : null}
      </div>

      <KittingInventoryDiscrepancyModal
        open={discrepancyModalOpen}
        initialSku={sku}
        onClose={() => setDiscrepancyModalOpen(false)}
      />
    </div>
  );
}
